import React from 'react';
import ImageCropBox, { ImageCrop } from './ImageCropBox';

interface ClipImage {
  id: string;
  layer: string;
  imageUrl: string;
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  rotation: number;
  startTime: number;
  endTime: number | null;
  segmentIds: string[];
  /** Sub-rect of the source image that gets drawn; the box on the frame is unaffected. */
  crop: ImageCrop;
}

interface PanelSegment {
  id: string;
  index: number;
  startTime: number;
  endTime: number;
}

interface ImageOverlayPanelProps {
  image: ClipImage;
  duration: number;
  segments: PanelSegment[];
  onUpdate: (image: ClipImage) => void;
  onDelete: (id: string) => void;
  /** True while the crop rect is being dragged on the video frame instead of here. */
  cropping: boolean;
  onToggleCropOnFrame: () => void;
  onClose: () => void;
}

const ImageOverlayPanel: React.FC<ImageOverlayPanelProps> = ({
  image, duration, segments, onUpdate, onDelete, cropping, onToggleCropOnFrame, onClose,
}) => {
  // The binding lives on the image itself — segment ids, not positions — so it survives a save
  // and follows those segments when they are split, merged or reordered. Ticking any segment
  // drives Start / End, so the two number inputs go read-only until every tick is cleared.
  const boundIds = image.segmentIds || [];
  const boundBySegment = boundIds.length > 0;
  const boundNumbers = segments
    .filter(sg => boundIds.includes(sg.id))
    .map(sg => sg.index + 1)
    .sort((a, b) => a - b);

  const toggleSegment = (id: string) => {
    const next = boundIds.includes(id)
      ? boundIds.filter(i => i !== id)
      : [...boundIds, id];
    const picked = segments.filter(sg => next.includes(sg.id));
    onUpdate({
      ...image,
      segmentIds: next,
      ...(picked.length > 0 ? {
        startTime: Math.min(...picked.map(sg => sg.startTime)),
        endTime: Math.max(...picked.map(sg => sg.endTime)),
      } : {}),
    });
  };

  return (
    <div className="image-overlay-panel">
      <div className="panel-header">
        <h3>Image Overlay</h3>
        <button className="btn-icon" onClick={onClose}>✕</button>
      </div>

      <div className="control-row control-row--crop">
        <div className="crop-label-row">
          <label>Crop</label>
          <button
            className={`btn-sm ${cropping ? 'btn-accent' : ''}`}
            onClick={onToggleCropOnFrame}
            title="Drag the crop rect on the video frame instead"
          >
            {cropping ? 'Cropping on frame' : 'Crop on frame'}
          </button>
        </div>
        <ImageCropBox
          src={image.src}
          crop={image.crop || { x: 0, y: 0, w: 1, h: 1 }}
          onChange={(crop) => onUpdate({ ...image, crop })}
        />
      </div>

      <div className="control-row">
        <label>Layer</label>
        <select
          value={image.layer}
          onChange={(e) => onUpdate({ ...image, layer: e.target.value })}
        >
          <option value="back">Back</option>
          <option value="middle">Middle</option>
          <option value="front">Front</option>
        </select>
      </div>

      <div className="control-row">
        <label>Opacity</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={image.opacity}
          onChange={(e) => onUpdate({ ...image, opacity: parseFloat(e.target.value) })}
        />
        <span>{Math.round(image.opacity * 100)}%</span>
      </div>

      <div className="control-row">
        <label>Rotation</label>
        <input
          type="range"
          min="-180"
          max="180"
          step="1"
          value={image.rotation}
          onChange={(e) => onUpdate({ ...image, rotation: parseInt(e.target.value) })}
        />
        <span>{image.rotation}°</span>
      </div>

      <div className="control-row control-row--segments">
        <label>Segments</label>
        <div className="segment-pick-list">
          {segments.map(sg => (
            <button
              key={sg.id}
              className={`segment-pick ${boundIds.includes(sg.id) ? 'segment-pick--on' : ''}`}
              onClick={() => toggleSegment(sg.id)}
              title={`Show the image for the whole of segment ${sg.index + 1}`}
            >
              {sg.index + 1}
            </button>
          ))}
        </div>
      </div>
      {boundBySegment && (
        <p className="panel-hint">
          Timed to {boundNumbers.length === 1 ? 'segment' : 'segments'}{' '}
          {boundNumbers.join(', ')} — the range follows them through split, merge and reorder.
          Clear them to set times by hand.
        </p>
      )}

      <div className="control-row">
        <label>Start</label>
        <input
          type="number"
          step="0.1"
          min="0"
          max={duration}
          value={image.startTime.toFixed(1)}
          disabled={boundBySegment}
          onChange={(e) => onUpdate({ ...image, startTime: parseFloat(e.target.value) })}
        />
      </div>

      <div className="control-row">
        <label>End</label>
        <input
          type="number"
          step="0.1"
          min="0"
          max={duration}
          value={(image.endTime ?? duration).toFixed(1)}
          disabled={boundBySegment}
          onChange={(e) => onUpdate({ ...image, endTime: parseFloat(e.target.value) })}
        />
      </div>

      <button className="btn-sm btn-danger" onClick={() => onDelete(image.id)}>Delete Image</button>
    </div>
  );
};

export default ImageOverlayPanel;
